// src/pages/solicitudes/FiltrosSolicitudes.js
import React, { useEffect, useState, useRef } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Collapse from 'react-bootstrap/Collapse'
import api from 'api/api'

// Cache para las opciones de filtros (evita llamadas multiples)
let opcionesFiltrosCache = null

/**
 * Componente de filtros para las listas de solicitudes
 */
function FiltrosSolicitudes({
	filtroCategoria,
	setFiltroCategoria,
	filtroCiudad,
	setFiltroCiudad,
	filtroTipo,
	setFiltroTipo,
	filtroEstado,
	setFiltroEstado,
	estadosDisponibles = [],
	mostrarFiltroEstado = true,
	filtroFechaDesde,
	setFiltroFechaDesde,
	filtroFechaHasta,
	setFiltroFechaHasta,
	mostrarFiltroFechas = false
}) {
	const [mostrarFiltros, setMostrarFiltros] = useState(false)
	const [categorias, setCategorias] = useState(opcionesFiltrosCache?.categorias || [])
	const [ciudades, setCiudades] = useState(opcionesFiltrosCache?.ciudades || [])
	const [loadingFiltros, setLoadingFiltros] = useState(false)

	const cargandoRef = useRef(false)

	// Cargar opciones de filtros al montar (solo si no estan en cache)
	useEffect(() => {
		if (!opcionesFiltrosCache && !cargandoRef.current) {
			cargarOpcionesFiltros()
		}
	}, [])

	const cargarOpcionesFiltros = async () => {
		if (cargandoRef.current) return
		cargandoRef.current = true

		try {
			setLoadingFiltros(true)
			const { datos, error } = await api.get('a/config-solicitudes')

			if (!error && datos) {
				opcionesFiltrosCache = {
					categorias: datos.categorias || [],
					ciudades: datos.ciudades || []
				}
				setCategorias(opcionesFiltrosCache.categorias)
				setCiudades(opcionesFiltrosCache.ciudades)
			}
		} catch (error) {
			console.error('Error al cargar filtros:', error)
		} finally {
			setLoadingFiltros(false)
			cargandoRef.current = false
		}
	}

	const limpiarFiltros = () => {
		setFiltroCategoria('')
		setFiltroCiudad('')
		setFiltroTipo('')
		if (setFiltroEstado) setFiltroEstado('')
		if (setFiltroFechaDesde) setFiltroFechaDesde('')
		if (setFiltroFechaHasta) setFiltroFechaHasta('')
	}

	const hayFiltrosActivos = () => {
		return filtroCategoria || filtroCiudad || filtroTipo || filtroEstado || filtroFechaDesde || filtroFechaHasta
	}

	// Mapeo de estados para mostrar nombres legibles
	const nombresEstados = {
		pendiente: 'Pendiente',
		confirmando_pago: 'Confirmando pago',
		pagado: 'Pagado',
		aceptado: 'Aceptado',
		agendado: 'Agendado',
		completado: 'Completado',
		cancelado: 'Cancelado',
		abandonado: 'Abandonado'
	}

	return (
		<Row className="mb-3">
			<Col xs={12}>
				<Button
					variant="link"
					onClick={() => setMostrarFiltros(!mostrarFiltros)}
					className="p-0 text-decoration-none"
				>
					<i className={`fa fa-filter me-2`}></i>
					{mostrarFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}
					{hayFiltrosActivos() && (
						<span className="badge bg-primary ms-2">Filtros activos</span>
					)}
				</Button>

				<Collapse in={mostrarFiltros}>
					<div className="mt-3 p-3 bg-light rounded">
						<Row className="g-3">
							{/* Filtro por Categoria */}
							<Col md={3}>
								<Form.Group>
									<Form.Label className="small fw-bold">Categoria</Form.Label>
									<Form.Select
										size="sm"
										value={filtroCategoria}
										onChange={(e) => setFiltroCategoria(e.target.value)}
										disabled={loadingFiltros}
									>
										<option value="">Todas las categorias</option>
										{categorias.map((cat) => (
											<option key={cat.id} value={cat.id}>
												{cat.nombre}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>

							{/* Filtro por Ciudad */}
							<Col md={3}>
								<Form.Group>
									<Form.Label className="small fw-bold">Ciudad</Form.Label>
									<Form.Select
										size="sm"
										value={filtroCiudad}
										onChange={(e) => setFiltroCiudad(e.target.value)}
										disabled={loadingFiltros}
									>
										<option value="">Todas las ciudades</option>
										{ciudades.map((ciudad) => (
											<option key={ciudad.id} value={ciudad.id}>
												{ciudad.nombre}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>

							{/* Filtro por Tipo */}
							<Col md={3}>
								<Form.Group>
									<Form.Label className="small fw-bold">Tipo</Form.Label>
									<Form.Select
										size="sm"
										value={filtroTipo}
										onChange={(e) => setFiltroTipo(e.target.value)}
									>
										<option value="">Todos los tipos</option>
										<option value="urgencia">Urgencia</option>
										<option value="presupuesto">Presupuesto</option>
										<option value="evaluacion">Evaluacion</option>
									</Form.Select>
								</Form.Group>
							</Col>

							{/* Filtro por Estado */}
							{mostrarFiltroEstado && estadosDisponibles.length > 0 && (
								<Col md={3}>
									<Form.Group>
										<Form.Label className="small fw-bold">Estado</Form.Label>
										<Form.Select
											size="sm"
											value={filtroEstado}
											onChange={(e) => setFiltroEstado(e.target.value)}
										>
											<option value="">Todos los estados</option>
											{estadosDisponibles.map((estado) => (
												<option key={estado} value={estado}>
													{nombresEstados[estado] || estado}
												</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>
							)}

							{/* Filtros por Fecha */}
							{mostrarFiltroFechas && (
								<>
									<Col md={3}>
										<Form.Group>
											<Form.Label className="small fw-bold">Fecha desde</Form.Label>
											<Form.Control
												type="date"
												size="sm"
												value={filtroFechaDesde}
												onChange={(e) => setFiltroFechaDesde(e.target.value)}
											/>
										</Form.Group>
									</Col>
									<Col md={3}>
										<Form.Group>
											<Form.Label className="small fw-bold">Fecha hasta</Form.Label>
											<Form.Control
												type="date"
												size="sm"
												value={filtroFechaHasta}
												onChange={(e) => setFiltroFechaHasta(e.target.value)}
											/>
										</Form.Group>
									</Col>
								</>
							)}

							{/* Boton limpiar */}
							<Col md={3} className="d-flex align-items-end">
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={limpiarFiltros}
									disabled={!hayFiltrosActivos()}
								>
									<i className="fa fa-times me-1"></i>
									Limpiar filtros
								</Button>
							</Col>
						</Row>
					</div>
				</Collapse>
			</Col>
		</Row>
	)
}

export default FiltrosSolicitudes